import axios from "axios";
import { useContext, useEffect, useState } from "react";
import { Alert, Button, Form, InputGroup } from "react-bootstrap";
import { Spinner } from "react-bootstrap";
import { FaArrowRight, FaEye, FaEyeSlash, FaLock } from "react-icons/fa";
import { MdEmail } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import "../../../../assets/css/customer/auth/LoginPage.css";
import AppContext from "../../../../contexts/AppContext";

const LoginPage = () => {
  const navigate = useNavigate();
  const {
    baseUrl,
    setCustomerToken,
    setIsAuthenticated,
    setCustomer,
    isAuthenticated,
    customer,
  } = useContext(AppContext);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const handleLogin = () => {
    setErrorMessage("");

    if (!email.trim() || !password.trim()) {
      setErrorMessage("Please enter both email and password.");
      return;
    }

    setLoginLoading(true);

    const formData = new FormData();
    formData.append("email", email);
    formData.append("password", password);

    axios
      .post(`${baseUrl}/customer/login`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then((response) => {
        setLoginLoading(false);

        if (response.data.customer == null) {
          setErrorMessage(
            response.data.message || "Invalid email or password",
          );
          return;
        }

        // setCustomerToken already persists the token to localStorage
        // under "customerAuthToken" — no need to write it here too.
        setCustomerToken(response.data.token);
        localStorage.setItem(
          "loggedInCustomer",
          JSON.stringify(response.data.customer),
        );
        setCustomer(response.data.customer);
        setIsAuthenticated(true);
        navigate("/");
      })
      .catch((error) => {
        console.log(error);
        setLoginLoading(false);
        setErrorMessage(
          error.response?.data?.message || "Something went wrong. Try again.",
        );
      });
  };

  useEffect(() => {
    // AppContext already rehydrates isAuthenticated/customer from
    // localStorage ("customerAuthToken" + "loggedInCustomer") on mount,
    // so we just redirect if that state says the user is already logged in.
    if (isAuthenticated && customer) {
      navigate("/");
      return;
    }
    setPageLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return loginLoading || pageLoading ? (
    <div
      style={{ height: "100vh" }}
      className="d-flex justify-content-center align-items-center"
    >
      <Spinner style={{ height: 80, width: 80, fontSize: 60 }} />
    </div>
  ) : (
    <div className="glassmorphism-login-container">
      <div className="background-circles">
        <div className="circle circle-1"></div>
        <div className="circle circle-2"></div>
        <div className="circle circle-3"></div>
        <div className="circle circle-4"></div>
      </div>

      <div className="login-content-wrapper">
        <div className="brand-section">
          <h1 className="logo-text">CRAFTHIVE</h1>
          <h2 className="main-heading">
            Welcome back to
            <br />
            handmade treasures
          </h2>
          <p className="description-text">
            Log in to explore and shop unique handmade finds.
          </p>
        </div>

        <div className="form-section">
          <div className="glass-card">
            <h3 className="form-title">WELCOME BACK</h3>
            <p className="form-subtitle">Login to continue shopping</p>

            <Form
              className="login-form"
              onSubmit={(e) => {
                e.preventDefault();
                handleLogin();
              }}
            >
              {errorMessage && (
                <Alert variant="danger" className="mb-3">
                  {errorMessage}
                </Alert>
              )}
              <Form.Group className="mb-0">
                <InputGroup>
                  <InputGroup.Text className="input-group-icon">
                    <MdEmail size={20} />
                  </InputGroup.Text>
                  <Form.Control
                    type="email"
                    name="email"
                    placeholder="Email Address"
                    className="glass-input"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setErrorMessage("");
                    }}
                    required
                  />
                </InputGroup>
              </Form.Group>
              <Form.Group className="mb-0">
                <InputGroup>
                  <InputGroup.Text className="input-group-icon">
                    <FaLock size={18} />
                  </InputGroup.Text>
                  <Form.Control
                    type={showPassword ? "text" : "password"}
                    name="password"
                    placeholder="Password"
                    className="glass-input"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setErrorMessage("");
                    }}
                    required
                  />
                  <Button
                    variant="link"
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <FaEyeSlash size={18} />
                    ) : (
                      <FaEye size={18} />
                    )}
                  </Button>
                </InputGroup>
              </Form.Group>
              <Button
                type="submit"
                className="login-button w-100"
                disabled={loginLoading}
              >
                {loginLoading ? "LOGGING IN..." : "LOGIN"}
                <FaArrowRight size={18} className="ms-2 btn-arrow" />
              </Button>
            </Form>

            <div className="signup-link-section">
              <span className="signup-text">Don't have an account? </span>
              <a
                href="/signup"
                className="signup-link"
                onClick={(e) => {
                  e.preventDefault();
                  navigate("/signup");
                }}
              >
                Sign up now
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;